import type { ComponentProps } from 'react';
import React, { useCallback, useMemo } from 'react';

import type { AIVoiceFilterOption } from 'js/src/components/app/VoiceSelectionModal/useAIVoiceFilters';
import Select from 'js/src/components/common/ui/Select/Select';
import type { GroupBase, SelectOption } from 'js/src/components/common/ui/Select/types';
import type {
    ChangeFilterCallback,
    ChangeMultiFilterCallback,
    SelectedFilterMultiValueType,
    SelectedFilterValueType,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-types';

type SelectValueType = AIVoiceFilterOption['value'];
type SelectComponentPropsType = ComponentProps<typeof Select<SelectValueType>>;
type MultiSelectComponentPropsType = ComponentProps<
    typeof Select<SelectValueType, SelectOption<SelectValueType>, true>
>;

type SelectOptionType = SelectOption<SelectValueType>;
type SelectGroupType = GroupBase<SelectOptionType>;
type SelectChangeCallbackType = NonNullable<SelectComponentPropsType['onChange']>;
type MultiSelectChangeCallbackType = NonNullable<MultiSelectComponentPropsType['onChange']>;

const ANY_OPTION: SelectOptionType = { label: 'Any', value: 'any' };

interface AIVoiceFilterDropdownBaseProps {
    options: AIVoiceFilterOption[];
    placeholder: string;
    iconLeft?: string;
    className?: string;
    menuPortalTarget?: HTMLElement | null;
}

interface AIVoiceFilterDropdownSingleProps extends AIVoiceFilterDropdownBaseProps {
    selectedValue: SelectedFilterValueType;
    onChange: ChangeFilterCallback;
    includeAny?: boolean;
    isSearchable?: boolean;
}

interface AIVoiceFilterDropdownMultiProps extends AIVoiceFilterDropdownBaseProps {
    selectedValue: SelectedFilterMultiValueType;
    onChange: ChangeMultiFilterCallback;
}

type AIVoiceFilterDropdownProps =
    | (AIVoiceFilterDropdownSingleProps & { isMulti?: false })
    | (AIVoiceFilterDropdownMultiProps & { isMulti: true });

interface UseSelectOptionsConfig {
    placeholder: string;
    includeAny: boolean;
}

function useSelectOptions(
    options: AIVoiceFilterDropdownBaseProps['options'],
    config: UseSelectOptionsConfig,
): SelectGroupType[] {
    const includeAny = config?.includeAny ?? false;
    return useMemo(() => {
        const groups: SelectGroupType[] = [];

        if (includeAny) {
            groups.push({
                options: [ANY_OPTION],
            });
        }

        const filterOptions = options.map((option) => ({
            label: option.display,
            value: option.value,
        }));
        groups.push({
            options: filterOptions,
        });

        return groups;
    }, [includeAny, options]);
}

function AIVoiceFilterSingleSelectDropdown({
    options,
    selectedValue,
    onChange,
    placeholder,
    iconLeft,
    className,
    menuPortalTarget,
    includeAny = true,
    isSearchable = true,
}: AIVoiceFilterDropdownSingleProps) {
    const selectOptions = useSelectOptions(options, {
        placeholder,
        includeAny,
    });

    const selectedOption = useMemo(() => {
        if (!selectedValue) {
            return undefined;
        }
        const selectedOption = selectOptions
            .flatMap((group) => group.options)
            .find((option) => option.value === selectedValue);
        return selectedOption ?? undefined;
    }, [selectOptions, selectedValue]);

    const handleChange = useCallback<SelectChangeCallbackType>(
        (value) => {
            onChange(value === ANY_OPTION.value ? null : value);
        },
        [onChange],
    );

    return (
        <Select<SelectValueType>
            key={selectedValue}
            isMulti={false}
            options={selectOptions}
            value={selectedOption}
            onChange={handleChange}
            placeholder={placeholder}
            className={className}
            isSearchable={isSearchable}
            iconLeft={iconLeft}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
        />
    );
}

function AIVoiceFilterMultiSelectDropdown({
    options,
    selectedValue,
    onChange,
    placeholder,
    iconLeft,
    className,
    menuPortalTarget,
}: AIVoiceFilterDropdownMultiProps) {
    const selectOptions = useSelectOptions(options, {
        placeholder,
        includeAny: false,
    });

    const selectedOptions = useMemo(() => {
        if (!selectedValue || selectedValue.length === 0) {
            return [];
        }

        return selectOptions
            .flatMap((group) => group.options)
            .filter((option) => selectedValue.includes(option.value));
    }, [selectOptions, selectedValue]);

    const handleChange = useCallback<MultiSelectChangeCallbackType>(
        (values) => {
            onChange(values);
        },
        [onChange],
    );

    return (
        <Select<SelectValueType, SelectOptionType, true, SelectGroupType>
            isMulti
            options={selectOptions}
            value={selectedOptions}
            onChange={handleChange}
            placeholder={placeholder}
            className={className}
            isSearchable={true}
            iconLeft={iconLeft}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
        />
    );
}

const AIVoiceFilterDropdown = React.memo(function AIVoiceFilterDropdown(
    props: AIVoiceFilterDropdownProps,
) {
    return (
        <div className="ai-voice-filter-dropdown">
            {props.isMulti ? (
                <AIVoiceFilterMultiSelectDropdown {...props} />
            ) : (
                <AIVoiceFilterSingleSelectDropdown {...props} />
            )}
        </div>
    );
});

export default AIVoiceFilterDropdown;
